import { CircularProgress, Divider, Stack, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { useCommonApi } from '../api';
import { BoxWithHeader, PageLayout } from './common';
import { DateRangeToolbar } from './common/DateRangeToolbar';
import { useAuth } from './hooks';
import { useDateRange } from './hooks/useDateRange';
import { AllDealersAccidentsBestTipers } from './statisctics/AllDealersAccidentBestTipers';
import { AllDealersAccidentReasonAcceptanceOverview } from './statisctics/AllDealersAccidentReasonAcceptanceOverview';
import { AllDealersAccidentReasonDeniedOverview } from './statisctics/AllDealersAccidentReasonDeniedOverview';
import { AllDealersAccidentsCountOverview } from './statisctics/AllDealersAccidentsCountOverview';
import { AllDealersAccidentsReasonsOverview } from './statisctics/AllDealersAccidentsReasonsOverview';
import { AllDealersRentalsCountOverview } from './statisctics/AllDealersRentalsCountOverview';
import { CarsByBranchPie } from './statisctics/CarsByBranchPie';
import { DealerAccidentsBestTipers } from './statisctics/DealerAccidentsBestTipers';
import { DealerAccidentsOverview } from './statisctics/DealerAccidentsOverview';
import { DealerAccidentsOverviewOnPlacePie } from './statisctics/DealerAccidentsOverviewOnPlacePie';
import { DealerAccidentsOverviewPie } from './statisctics/DealerAccidentsOverviewPie';
import { RentalsByBranchPie } from './statisctics/RentalsByBranchPie';
import { Role } from './types';

const StatisticsPageContent = () => {
  const { user } = useAuth();

  const { range: dateRange, setCustom } = useDateRange({
    weekStartsOn: 1,
    initialPreset: 'thisMonth',
    fmt: 'yyyy-MM-dd'
  });

  const { data: dealerStatistics, isPending: isLoadingData } = useQuery(
    useCommonApi().getDealerStatistics({
      dateFrom: format(dateRange[0], 'yyyy-MM-dd'),
      dateTo: format(dateRange[1], 'yyyy-MM-dd'),
      dealerId: user?.id as string
    })
  );

  const selectedPeriodString = [format(dateRange[0], 'dd.MM.yyyy'), format(dateRange[1], 'dd.MM.yyyy')].join(' - ');

  if (user?.role !== Role.Dealer) return null;
  return (
    <PageLayout
      title="Statistiky"
      extra={
        <Stack direction={{ xs: 'column-reverse', md: 'row' }} gap={2}>
          <DateRangeToolbar
            value={dateRange}
            onChange={(r) => {
              const [f, t] = r;
              setCustom(new Date(f), new Date(t));
            }}
            weekStartsOn={1}
          />
        </Stack>
      }
    >
      {isLoadingData && !dealerStatistics ? (
        <Stack direction="row" justifyContent="center" mt={4}>
          <CircularProgress />
        </Stack>
      ) : !dealerStatistics ? (
        <Stack width={1} justifyContent="center">
          <Typography color="gray" textAlign="center">
            Vyberte časový úsek...
          </Typography>
        </Stack>
      ) : (
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6, lg: 3 }}>
            <BoxWithHeader
              title={
                <>
                  <Typography variant="h6" fontWeight="bold">
                    Celkový počet hlášených
                  </Typography>
                  <Typography variant="body2" color="gray">
                    V čase v celé společnosti
                  </Typography>
                </>
              }
            >
              <Typography variant="h4" fontWeight="700">
                {dealerStatistics.totals.countTotalByAllDealers.count}
              </Typography>
            </BoxWithHeader>
          </Grid>
          <Grid size={{ xs: 12, md: 6, lg: 3 }}>
            <BoxWithHeader
              title={
                <>
                  <Typography variant="h6" fontWeight="bold">
                    Celkový počet navštívených
                  </Typography>
                  <Typography variant="body2" color="gray">
                    V čase v celé společnosti
                  </Typography>
                </>
              }
            >
              <Typography variant="h4" fontWeight="700">
                {dealerStatistics.totals.countSpotSolvedByAllDealers.count}
              </Typography>
            </BoxWithHeader>
          </Grid>
          <Grid size={{ xs: 12, md: 6, lg: 3 }}>
            <BoxWithHeader
              title={
                <>
                  <Typography variant="h6" fontWeight="bold">
                    Celkový počet zapůjčených
                  </Typography>
                  <Typography variant="body2" color="gray">
                    V čase v celé společnosti
                  </Typography>
                </>
              }
            >
              <Typography variant="h4" fontWeight="700">
                {dealerStatistics?.totals?.countRentalsByAllDealers?.values?.reduce((total, item) => {
                  return total + (item.types?.rent || 0);
                }, 0)}
              </Typography>
            </BoxWithHeader>
          </Grid>
          <Grid size={{ xs: 12, md: 6, lg: 3 }}>
            <BoxWithHeader
              title={
                <>
                  <Typography variant="h6" fontWeight="bold">
                    Celkový počet čekajících
                  </Typography>
                  <Typography variant="body2" color="gray">
                    V čase v celé společnosti
                  </Typography>
                </>
              }
            >
              <Typography variant="h4" fontWeight="700">
                {dealerStatistics?.totals?.countRentalsByAllDealers?.values?.reduce((total, item) => {
                  return total + (item.types?.waiting || 0);
                }, 0)}
              </Typography>
            </BoxWithHeader>
          </Grid>
          <Grid size={12} my={3}>
            <Divider />
          </Grid>
          <Grid size={12}>
            <AllDealersRentalsCountOverview graphData={dealerStatistics} date={selectedPeriodString} />
          </Grid>
          <Grid size={12} my={3}>
            <Divider />
          </Grid>
          <Grid size={{ xs: 12, lg: 6 }}>
            <DealerAccidentsOverview graphData={dealerStatistics} />
          </Grid>
          <Grid size={{ xs: 12, lg: 3 }}>
            <DealerAccidentsOverviewPie graphData={dealerStatistics} />
          </Grid>
          <Grid size={{ xs: 12, lg: 3 }}>
            <DealerAccidentsOverviewOnPlacePie graphData={dealerStatistics} />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <AllDealersAccidentReasonAcceptanceOverview graphData={dealerStatistics} date={selectedPeriodString} />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <AllDealersAccidentReasonDeniedOverview graphData={dealerStatistics} date={selectedPeriodString} />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <DealerAccidentsBestTipers graphData={dealerStatistics} date={selectedPeriodString} />
          </Grid>
          <Grid size={12} my={3}>
            <Divider />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <AllDealersAccidentsCountOverview graphData={dealerStatistics} date={selectedPeriodString} />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <AllDealersAccidentsReasonsOverview graphData={dealerStatistics} date={selectedPeriodString} />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <AllDealersAccidentsBestTipers graphData={dealerStatistics} date={selectedPeriodString} />
          </Grid>
          <Grid size={12} my={3}>
            <Divider />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <CarsByBranchPie graphData={dealerStatistics} />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <RentalsByBranchPie graphData={dealerStatistics} date={selectedPeriodString} />
          </Grid>
        </Grid>
      )}
    </PageLayout>
  );
};

export const StatisticsPage = () => {
  return <StatisticsPageContent />;
};
